import React from 'react';
import { Layer, Line, Text, Rect } from 'react-konva';

interface TraceRulerProps {
    enabled: boolean;
    mousePosition: { x: number; y: number } | null;
    canvasWidth: number;
    canvasHeight: number;
    color: string;
    spacing: number;
    scale: number;
    offset: { x: number; y: number };
}

const TraceRuler: React.FC<TraceRulerProps> = ({
    enabled,
    mousePosition,
    canvasWidth,
    canvasHeight,
    color,
    spacing,
    scale,
    offset,
}) => {
    if (!enabled || !mousePosition) {
        return null;
    }

    // Calculate canvas position from mouse position
    const canvasX = mousePosition.x;
    const canvasY = mousePosition.y;

    // Calculate actual image coordinates (accounting for scale and offset)
    const imageX = (canvasX - offset.x) / scale;
    const imageY = (canvasY - offset.y) / scale;

    // Generate grid lines
    const verticalLines: number[] = [];
    const horizontalLines: number[] = [];

    // Calculate number of lines based on spacing
    for (let x = canvasX % spacing; x < canvasWidth; x += spacing) {
        if (Math.abs(x - canvasX) > 5) {
            // Skip lines too close to the main crosshair
            verticalLines.push(x);
        }
    }

    for (let y = canvasY % spacing; y < canvasHeight; y += spacing) {
        if (Math.abs(y - canvasY) > 5) {
            // Skip lines too close to the main crosshair
            horizontalLines.push(y);
        }
    }

    // Position label styling
    const labelPadding = 8;
    const labelMargin = 20;
    const labelX = labelMargin;
    const labelY = labelMargin;
    const labelText = `X: ${Math.round(imageX)}px, Y: ${Math.round(imageY)}px`;

    // Estimate text width (rough calculation)
    const textWidth = labelText.length * 7 + labelPadding * 2 + 30;
    const textHeight = 20;

    return (
        <Layer>
            {/* Main crosshair lines */}
            <Line
                points={[canvasX, 0, canvasX, canvasHeight]}
                stroke={color}
                strokeWidth={2}
                dash={[10, 5]}
                listening={false}
            />
            <Line
                points={[0, canvasY, canvasWidth, canvasY]}
                stroke={color}
                strokeWidth={2}
                dash={[10, 5]}
                listening={false}
            />

            {/* Grid lines - vertical */}
            {verticalLines.map((x, index) => (
                <Line
                    key={`v-${index}`}
                    points={[x, 0, x, canvasHeight]}
                    stroke={color}
                    strokeWidth={1}
                    opacity={0.3}
                    dash={[5, 10]}
                    listening={false}
                />
            ))}

            {/* Grid lines - horizontal */}
            {horizontalLines.map((y, index) => (
                <Line
                    key={`h-${index}`}
                    points={[0, y, canvasWidth, y]}
                    stroke={color}
                    strokeWidth={1}
                    opacity={0.3}
                    dash={[5, 10]}
                    listening={false}
                />
            ))}

            {/* Coordinate display label */}
            <Rect
                x={labelX}
                y={labelY}
                width={textWidth}
                height={textHeight}
                fill='rgba(0, 0, 0, 0.75)'
                cornerRadius={4}
                listening={false}
            />
            <Text
                x={labelX + labelPadding}
                y={labelY + 3}
                text={labelText}
                fontSize={14}
                fontFamily='monospace'
                fill='#FFFFFF'
                listening={false}
            />
        </Layer>
    );
};

export default TraceRuler;
